/**
 * Anfragen: Tabelle mit Status-Tabs (Stitch: maestro_anfragen). F2 (docs/PLAN-ANFRAGE-VORGANG-
 * EINE-IDENTITAET.md): der Zeilenklick fuehrt jetzt DIREKT auf die eine Vorgangsseite
 * (`vorgangPfad`, `pages/event-detail.tsx`) — `pages/anfrage-detail.tsx` ist geloescht, `id` ist
 * seit AP5 bereits die Event-ID (kein `generatedEventId`-Umweg mehr noetig).
 * + "Anfrage anlegen" als Drawer-Formular (POST /api/inquiries).
 * Statuswechsel via PATCH /api/inquiries/:id — sitzt jetzt auf der Vorgangsseite.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import type { StandAktion } from "@maestro/anfrage-stand";
import { useApi, apiMutate } from "../hooks/useApi";
import { apiFetch } from "../lib/api";
import { angebotPfad, uebersetzeAltesZiel, vorgangPfad } from "../lib/pfade";
import { useContentRefetch } from "../ui/CopilotProvider";
import {
  ANFRAGEN_SORTIERUNG,
  AnfragenGruppenAnsicht,
  teileInGruppen,
  fasseFuerAnzeigeZusammen,
  useAnfragenSortierung,
} from "../ui/AnfragenGruppen";
import { AnfragenKanban, type KanbanMenuAktion } from "../ui/AnfragenKanban";
import { GrundNotierenDialog } from "../ui/GrundNotieren";
import { AnfrageAntwortDrawer } from "../ui/AnfrageAntwortDrawer";
import { oeffneGlobaleSuche } from "../lib/globale-suche";
import { useUrlZustand } from "../hooks/useUrlZustand";
import type { Customer, Inquiry } from "../ui/types";
import { eventStatusMeta, formatDate, inquirySourceMeta, money } from "../ui/format";
import { DatumVergangenHinweis } from "../ui/DatumVergangenHinweis";
import { Button, Card, ChipSelect, Drawer, EmptyState, ErrorNote, Icon, Input, LoadingRows, PageHeader, SortierMenue, Zeichenzaehler, useBestaetigung, DateInput, Radio, Textarea } from "../ui/components";

/** Kompakte Quellen-Labels für die Listen-Pill (Design 2b: „Formular"/„E-Mail" statt Langform). */
const SOURCE_SHORT: Record<string, string> = {
  manual: "Manuell",
  form: "Formular",
  chat: "KI-Chat",
  email: "E-Mail",
  phone: "Telefon",
};
/**
 * P10.1 (Entwurf 32a) — **es gibt nur noch eine Ansicht.**
 *
 * Bis zum 10.08.2026 standen hier zwei nebeneinander: die Status-Reiter („Alle · Neu · Angebot
 * gesendet · Gewonnen · Abgesagt") und die Gruppen-Ansicht „Wer ist dran", dazwischen ein
 * Umschalter. Das war Antoines Auflage aus dem Gate vor P5 (07.08.) und ausdrücklich vorläufig.
 * Mit dem Auftrag „Anfragen bitte umbauen wie in 32A" ist der Tausch entschieden.
 *
 * **Warum der Umschalter weg musste** (Bauplan § 12.1): er teilte eine Liste in zwei mentale
 * Modelle und ließ beim Wechsel jeweils die Hälfte verschwinden — Suche und Filter gab es nur
 * links, nächster Schritt und Aktion nur rechts. Dazu zwei Widersprüche, die beide belegt sind:
 * die Zahlen (Badge · „N offen" · „bei N wartet etwas auf dich") und die Statussprache. Der Reiter
 * „Angebot gesendet" las `inquiries.status = 'quoted'` und fragte `events.offer_sent_at` nie —
 * bei allen 17 Storia-Zeilen ist dieses Feld `null`, es ging also nie ein Angebot raus, und die
 * Liste behauptete bei elf davon das Gegenteil. Genau der Fehlertyp, den CLAUDE.md als häufigsten
 * dieses Systems führt: sieht aus wie erledigt, ist es nicht.
 *
 * Suche und die beiden Filter sind geblieben — sie stehen jetzt ÜBER den Gruppen und wirken auf
 * alle. Was ersatzlos entfällt, ist die Filterung nach `status`: sie war die Beschriftung, die
 * gelogen hat.
 */

/**
 * W2.5 — Herkunfts-Kennzeichnung: kam die Anfrage aus einem MAESTRO-Formular-Widget, sagt die
 * Liste WELCHES. Der Name ist Betreiber-Text aus dem Widget-Editor und wird als React-Kind
 * gerendert (kein dangerouslySetInnerHTML) — dieselbe Regel wie im Loader.
 */
/**
 * Punkt 100 (25.09.2026): ohne Widget faellt die Herkunft auf bekannte `sourceDetail`-Werte der
 * Altsystem-Weiterleitungen zurueck. Unbekannte Werte zeigen nichts — nie raten.
 */
const HERKUNFT_NACH_SOURCE_DETAIL: Record<string, string> = {
  "ristorantestoria-filmfest": "über Filmfest-Formular",
  "ristorantestoria-reisegruppen": "über Reisegruppen-Formular",
};

export function herkunftsText(i: Pick<Inquiry, "widgetName" | "sourceDetail">): string | null {
  const name = i.widgetName?.trim();
  if (name) return `über Formular „${name}“`;
  return (i.sourceDetail && HERKUNFT_NACH_SOURCE_DETAIL[i.sourceDetail]) || null;
}

/**
 * R0.2 — die Firma der anfragenden Seite (`inquiries.company`, seit R0.1 ausgeliefert).
 * Leerstring/Whitespace zaehlt als "nicht angegeben": eine Pille „Firmenkunde“ ueber einem
 * leeren Feld waere eine Behauptung. Der Wert ist GASTTEXT (bis 200 Zeichen) — er wird ueberall
 * als React-Kind gerendert (kein dangerouslySetInnerHTML) und in der Liste gekuerzt.
 */
function firmenname(i: Pick<Inquiry, "company">): string | null {
  const name = i.company?.trim();
  return name ? name : null;
}

/** Prefix des Quellen-Filters, wenn nach einem einzelnen Widget gefiltert wird. */
const WIDGET_FILTER = "widget:";

/**
 * Welche Ansicht (Liste/Kanban) zuletzt offen war — pro Nutzer, sql/358. `localStorage` ist NUR
 * der sofortige Fallback beim ersten Render (kein Warten auf die Netzantwort für den ersten
 * Frame); die Serverantwort (`/api/inquiries/ansicht-praeferenz`) überschreibt ihn danach, sobald
 * sie da ist — sie ist die Wahrheit bei Multi-Device-Nutzung, `localStorage` ist geräteweise.
 */
const ANSICHT_KEY = "maestro:anfragen:ansicht";
type Ansicht = "liste" | "kanban";
function leseAnsichtAusSpeicher(): Ansicht {
  try {
    return localStorage.getItem(ANSICHT_KEY) === "kanban" ? "kanban" : "liste";
  } catch {
    return "liste";
  }
}

/**
 * Punkt 121 (Antoine, 25.09.2026): der Kanal einer von Hand angelegten Anfrage ist Pflicht.
 * `manual` heißt „persönlich / sonstiges“ (ein eigener Wert `in_person` passt nicht in
 * inquiries_source_check). Die API nimmt genau diese drei Werte.
 */
export const KANAL_OPTIONEN = [
  { value: "phone", label: "Telefon", icon: "call" },
  { value: "email", label: "E-Mail", icon: "mail" },
  { value: "manual", label: "Persönlich / sonstiges", icon: "person" },
] as const;
type KanalWahl = (typeof KANAL_OPTIONEN)[number]["value"] | "";

/** Controlled-Form-Zustand des "Anfrage anlegen"-Drawers (damit der KI-Import Felder befuellen kann). */
const EMPTY_FORM = {
  customerName: "",
  customerEmail: "",
  company: "",
  // Migration 383 (Antoine, STORIA): Telefon + Anschrift aus dem E-Mail-Footer, wie die uebrigen
  // Felder vom KI-Import vorbefuellbar und vom Menschen editierbar/korrigierbar.
  phone: "",
  addressStreet: "",
  addressZip: "",
  addressCity: "",
  eventDate: "",
  guests: "",
  message: "",
};

/**
 * §5.16: die Diät-Schlüssel in Worten. Dieselben Labels wie im Komponisten-Prompt
 * (`DIAET_LABEL` in apps/api/src/lib/angebot-komponieren.ts) — der Betrieb soll dasselbe Wort
 * lesen, das auch das Modell zu sehen bekommt.
 */
const DIAET_TEXT: Record<string, string> = {
  vegetarian: "vegetarisch",
  vegan: "vegan",
  gluten_free: "glutenfrei",
  lactose_free: "laktosefrei",
};
type CreateForm = typeof EMPTY_FORM;

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/**
 * Punkt 79, Zusatz 6: die Zeile über dem Board — „126.750 € im Board · 2 Anzahlungen überfällig".
 *
 * NICHT in der Seitenüberschrift, und das ist keine Geschmacksfrage. P10.6 (Entwurf 32a) hat dort
 * jede Zählung abgeschafft: Badge `6` · Reiter „6 offen" · Kopfzeile „bei 17 wartet etwas auf
 * dich" waren drei Zahlen über dieselbe Liste, von denen keine zwei dasselbe meinten. Die
 * Zusicherung dagegen (`anfragen-zaehlwahrheit.test.tsx`) hat einen ersten Versuch, die Zahlen
 * aus dem Entwurf dort unterzubringen, sofort abgefangen — zu Recht.
 *
 * Was hier steht, ist deshalb GELD, keine Anzahl: eine Summe widerspricht keinem Zähler. Der
 * Auftrag sagt es genauso — „die Spaltensummen gibt es schon, nur die Addition DARÜBER fehlt".
 *
 * Reine Funktion, damit sie ohne die 1.000-Zeilen-Seite geprüft werden kann.
 */
export function boardSummeText(inquiries: readonly Inquiry[]): string | null {
  const summeCents = inquiries.reduce((s, i) => s + (i.amountTotalCents ?? 0), 0);
  const ueberfaellig = inquiries.filter((i) => Boolean(i.tatsachen?.anzahlungUeberfaelligSeit)).length;
  const teile: string[] = [];
  if (summeCents > 0) teile.push(`${money(summeCents)} im Board`);
  if (ueberfaellig > 0) teile.push(ueberfaellig === 1 ? "1 Anzahlung überfällig" : `${ueberfaellig} Anzahlungen überfällig`);
  return teile.length ? teile.join(" · ") : null;
}

/**
 * DIE LAGE-FILTER. Antoine am 21.09.2026, wörtlich: „Wir brauchen verschiedene Sortierungs/Filter
 * Möglichkeiten: Nach * Dringlichkeit * Bezahlt (wo wurde eine Zahlung vermerkt (Anzahlung,
 * Restzahlung etc.) * Unbezahlt (wo hätte Geld reinkommen sollen, kam aber nicht) * Fehler (wo gab
 * es Fehler/Probleme etc.) * Wo muss nachgehakt werden / Was fällt dir noch ein?" — dazu auf
 * Nachfrage: alle vier Fehlersorten, und **„Immer nur einer"** (die Filter sind nicht
 * kombinierbar). Die vier von mir ergänzten Vorschläge hat er freigegeben („ja, setze alle deine
 * Vorschläge um").
 *
 * JEDE dieser Fragen wird aus dem beantwortet, was der Server ohnehin liefert — dem Stand und den
 * Tatsachen. Keine neue Abfrage, kein neuer Parameter, und vor allem: **keine zweite Regel.** Ob
 * eine Mail nicht ankam oder ein Angebot leer ist, entscheidet nicht dieser Filter, sondern
 * dieselbe Zeile der Regel, die auch den Satz auf der Karte schreibt. Zwei Stellen, die „Fehler"
 * verschieden auslegen, wären genau der Widerspruch, den P10.6 bei den Zählern beseitigt hat.
 */
export type LageFilter =
  | "all"
  | "bezahlt"
  | "unbezahlt"
  | "fehler"
  | "nachhaken"
  | "laeuft_ab"
  | "nie_geoeffnet"
  | "termin_bald"
  | "ohne_termin";

/** Beschriftung und Reihenfolge im Auswahlfeld — Geld zuerst, dann Störungen, dann Termine. */
export const LAGE_FILTER_TEXT: ReadonlyArray<{ wert: LageFilter; text: string }> = [
  { wert: "all", text: "Lage: Alle" },
  { wert: "bezahlt", text: "Bezahlt" },
  { wert: "unbezahlt", text: "Unbezahlt" },
  { wert: "fehler", text: "Fehler" },
  { wert: "nachhaken", text: "Nachhaken" },
  { wert: "laeuft_ab", text: "Angebot läuft bald ab" },
  { wert: "nie_geoeffnet", text: "Nie geöffnet" },
  { wert: "termin_bald", text: "Termin steht bevor" },
  { wert: "ohne_termin", text: "Ohne Termin" },
];

/**
 * Der Notizen-Filter (Antoine, 25.10.2026, Mockup freigegeben): „Alle“, „Mit Notiz“, „Ohne Notiz“.
 * Liest `notizAnzahl` der Liste (Summe der drei Notizarten, serverseitig) — keine eigene Regel.
 * Ein eigenes Feld neben „Lage“, damit beide kombinierbar sind (Lage bleibt „immer nur einer“).
 */
export type NotizFilter = "all" | "mit" | "ohne";

export function trifftNotiz(anfrage: Pick<Inquiry, "notizAnzahl">, filter: NotizFilter): boolean {
  if (filter === "all") return true;
  const hatNotiz = (anfrage.notizAnzahl ?? 0) > 0;
  return filter === "mit" ? hatNotiz : !hatNotiz;
}

/** Beschriftung der Auswahl samt Zahlen („Mit Notiz (4)“), gezählt über alle geladenen Anfragen. */
export function notizFilterOptionen(anfragen: ReadonlyArray<Pick<Inquiry, "notizAnzahl">>): ReadonlyArray<{ wert: NotizFilter; text: string }> {
  const mit = anfragen.filter((a) => trifftNotiz(a, "mit")).length;
  return [
    { wert: "all", text: `Notizen: Alle (${anfragen.length})` },
    { wert: "mit", text: `Mit Notiz (${mit})` },
    { wert: "ohne", text: `Ohne Notiz (${anfragen.length - mit})` },
  ];
}

/** „Bald" heißt hier sieben Tage — dieselbe Spanne, die der Countdown auf der Karte anzeigt. */
const BALD_TAGE = 7;
/** „Termin steht bevor" heißt vierzehn Tage: zwei Wochen sind die Frist, in der noch etwas zu tun ist. */
const TERMIN_BALD_TAGE = 14;

/**
 * Trifft diese Anfrage den gewählten Lage-Filter? Reine Funktion mit übergebenem `jetzt`, damit
 * sie ohne die 1.000-Zeilen-Seite und ohne Uhr-Abhängigkeit geprüft werden kann.
 */
export function trifftLage(anfrage: Inquiry, filter: LageFilter, jetzt: number): boolean {
  if (filter === "all") return true;
  const t = anfrage.tatsachen;
  const regel = anfrage.stand?.regel;
  switch (filter) {
    case "bezahlt":
      // „wo wurde eine Zahlung vermerkt" — aber nur, wenn auch wirklich etwas einging. Eine
      // vereinbarte, nie bezahlte Rate ist das Gegenteil dessen, was hier gesucht wird.
      return (t?.bezahltCents ?? 0) > 0;
    case "unbezahlt":
      // „wo hätte Geld reinkommen sollen, kam aber nicht" — also überfällig, nicht bloß offen.
      // Ein Vorgang, dessen Rate erst nächsten Monat fällig ist, ist nicht unbezahlt.
      return Boolean(t?.zahlungUeberfaelligSeit);
    case "fehler":
      // Genau die vier Sorten, die Antoine genannt hat. Drei davon stehen als eigene Zeile in der
      // Regel, die vierte als Tatsache — keine wird hier neu hergeleitet.
      return (
        t?.zustellungFehlgeschlagen === true || // Mail kam nicht an
        t?.zahlungFehlgeschlagen === true || // Zahlung fehlgeschlagen
        regel === "zustellung_fehlgeschlagen" ||
        regel === "kein_angebot_gaeste_fehlen" || // Angaben fehlen
        regel === "angebot_leer" // Angebot leer
      );
    case "nachhaken":
      // Die Werktagsrechnung (mit Feiertagen je Bundesland) macht der Server. Hier wird sein
      // Ergebnis gelesen, nicht nachgerechnet — sonst gäbe es zwei Rechnungen, die auseinanderlaufen.
      return anfrage.stand?.chip === "Nachfassen fällig" || regel === "nachfassen";
    case "laeuft_ab": {
      if (!anfrage.offerValidUntil) return false;
      const bis = new Date(anfrage.offerValidUntil).getTime();
      if (Number.isNaN(bis)) return false;
      // Abgelaufen ist nicht „läuft bald ab": da ist die Frist bereits vorbei.
      return bis >= jetzt && bis - jetzt <= BALD_TAGE * 86_400_000;
    }
    case "nie_geoeffnet":
      // `null` heißt „nicht erhoben", `0` heißt „gezählt und nie geöffnet" — und vor dem Versand
      // ist „nie geöffnet" keine Auskunft, sondern eine Selbstverständlichkeit.
      return Boolean(t?.versendetAm) && t?.aufrufe === 0;
    case "termin_bald": {
      if (!anfrage.eventDate) return false;
      const termin = new Date(anfrage.eventDate).getTime();
      if (Number.isNaN(termin)) return false;
      return termin >= jetzt && termin - jetzt <= TERMIN_BALD_TAGE * 86_400_000;
    }
    case "ohne_termin":
      return !anfrage.eventDate;
  }
}

/** Antwort von `POST /api/inquiries/abgesagte-zurueckholen`. */
interface ZurueckholErgebnis {
  kandidaten: { id: string; customerName: string; eventDate: string | null; ziel: string | null; uebersprungen?: string }[];
  zurueckgeholt: number;
}

export default function InquiriesPage() {
  const { bestaetigen, dialog: bestaetigungsDialog } = useBestaetigung();
  const { data, loading, error, reload } = useApi<Inquiry[]>("/api/inquiries");
  // Chat-P1: update_inquiry (z.B. Status "abgesagt") per Chat -> Liste live nachladen.
  // Der Komponist aendert BEIDES: die Anfrage (status -> quoted, generatedEventId) und legt ein
  // Event an. Wer nur auf "inquiry" hoert, sieht nach einem Chat-Lauf weiter "KI-Angebot
  // erzeugen", obwohl das Angebot laengst existiert (Antoine, 02.08.2026, am Screenshot).
  useContentRefetch(["inquiry", "event"], reload);
  // Toolbar-Filter (E2): Freitextsuche + Wunschdatum- + Quelle-Filter. Seit P10.1 stehen sie über
  // allen Gruppen und wirken auf alle — es gibt keine zweite Ansicht mehr, die sie umgehen könnte.
  // Punkt 115 (Paket 3): Suche und Filter stehen in der Adresse (`useUrlZustand`) — nach „Zurück“
  // vom Vorgang sind sie wieder da. Schlüssel bewusst nicht `datum` (das belegt `?neu=1&datum=`).
  const [zustand, setzeZustand] = useUrlZustand({ q: "", zeitraum: "all", quelle: "all", lage: "all", notiz: "all" });
  const query = zustand.q;
  const setQuery = (q: string) => setzeZustand({ q });
  const dateFilter: "all" | "upcoming" | "past" =
    zustand.zeitraum === "upcoming" || zustand.zeitraum === "past" ? zustand.zeitraum : "all";
  const setDateFilter = (zeitraum: "all" | "upcoming" | "past") => setzeZustand({ zeitraum });
  const sourceFilter = zustand.quelle;
  const setSourceFilter = (quelle: string) => setzeZustand({ quelle });
  /**
   * Die Lage-Filter (21.09.2026, Antoine). EIN Auswahlfeld und nicht neun Schalter — auf die
   * Frage, ob die Filter kombinierbar sein sollen, hat er geantwortet: **„Immer nur einer"**. Ein
   * einzelnes Feld erzwingt das von selbst, statt es zu verbieten.
   */
  const lageFilter: LageFilter = LAGE_FILTER_TEXT.find((l) => l.wert === zustand.lage)?.wert ?? "all";
  const setLageFilter = (lage: LageFilter) => setzeZustand({ lage });
  /** Notizen-Filter (25.10.2026): wie „Lage“ im Adresszustand, wirkt auf Kanban und Liste gleich. */
  const notizFilter: NotizFilter = zustand.notiz === "mit" || zustand.notiz === "ohne" ? zustand.notiz : "all";
  const setNotizFilter = (notiz: NotizFilter) => setzeZustand({ notiz });
  /**
   * Die Sortierung (Listenstandard, Punkt 95). Seit dem 23.09.2026 ersetzen die Spaltenköpfe der
   * Liste die Auswahl vom 21.09.2026 (Antoine); Kanban und Telefon haben keine Spaltenköpfe und
   * bedienen denselben Zustand über ein Menü. Die VORGABE bleibt chronologisch — Begründung und
   * Spalten an `ANFRAGEN_SPALTEN` in `ui/AnfragenGruppen.tsx`.
   */
  const sort = useAnfragenSortierung();
  const [params, setParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(params.get("neu") === "1");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // Kalender verlinkt mit ?neu=1&datum=YYYY-MM-DD auf einen leeren Tag -> Wunschdatum vorbelegen.
  const [form, setForm] = useState<CreateForm>(() => {
    const datum = params.get("datum");
    return datum ? { ...EMPTY_FORM, eventDate: datum } : EMPTY_FORM;
  });
  const [kiText, setKiText] = useState("");
  const [kiBusy, setKiBusy] = useState(false);
  const [kiNote, setKiNote] = useState<string | null>(null);
  /**
   * §5.16: Was der Anfrage-Leser STRUKTURIERT erkannt hat. Bewusst neben dem Formular und nicht
   * darin: der Anlass wird zusaetzlich vorn an die Nachricht geklebt (unveraendertes Verhalten),
   * aber erst diese beiden Felder landen in `inquiries.event_type` / `dietary_requirements` und
   * damit im Prompt des Komponisten.
   */
  const [kiAnlass, setKiAnlass] = useState("");
  const [kanal, setKanal] = useState<KanalWahl>("");
  const [kiDiaet, setKiDiaet] = useState<string[]>([]);
  // P4.2 — die Zeile, für die gerade ein Ablehnungsgrund notiert wird (null = kein Formular offen).
  // Antoine 19.10.2026: Antwort-Drawer „Per E-Mail antworten“ (Menüpunkt und Klick auf „Antwort vom Gast“).
  const [antwortFuer, setAntwortFuer] = useState<Inquiry | null>(null);
  const [zurueckholenLaeuft, setZurueckholenLaeuft] = useState(false);
  const [grundFuer, setGrundFuer] = useState<Inquiry | null>(null);
  const [grundBusy, setGrundBusy] = useState(false);
  const [grundFehler, setGrundFehler] = useState<string | null>(null);
  const navigate = useNavigate();

  // Kanban-Umschalter (sql/358): sofort aus localStorage, dann von der Serverantwort ueberschrieben.
  const [ansicht, setAnsicht] = useState<Ansicht>(leseAnsichtAusSpeicher);
  const { data: ansichtVomServer } = useApi<{ ansicht: Ansicht }>("/api/inquiries/ansicht-praeferenz");
  // Fund 08.09.2026 (E2E-Verifikation): die GET-Antwort kann spaeter eintreffen als ein Klick auf
  // "Kanban"/"Liste" direkt nach dem Seitenaufbau (z. B. Neon-Kaltstart, ~21s laut CLAUDE.md) - traf
  // sie danach ein, ueberschrieb dieser Effekt den gerade gewaehlten Wert lautlos mit dem alten
  // Serverstand. `nutzerHatGewaehlt` sperrt den Server-Sync, sobald der Mensch einmal selbst
  // geklickt hat; ein echter Reload erzeugt eine neue Komponenteninstanz (Ref startet wieder bei
  // false), die Serverantwort bleibt dort weiterhin die Wahrheit.
  const nutzerHatGewaehlt = useRef(false);
  useEffect(() => {
    if (!nutzerHatGewaehlt.current && ansichtVomServer?.ansicht) setAnsicht(ansichtVomServer.ansicht);
  }, [ansichtVomServer]);

  function wechsleAnsicht(neu: Ansicht) {
    nutzerHatGewaehlt.current = true;
    setAnsicht(neu);
    try {
      localStorage.setItem(ANSICHT_KEY, neu);
    } catch {
      // Privater Modus o.ä. — kein Blocker, der Server-Roundtrip unten trägt trotzdem.
    }
    // Fire-and-forget, aber MIT `keepalive` (Fund 08.09.2026): ein Klick gefolgt von einem sofortigen
    // Reload/Navigate kann einen laufenden `fetch` sonst abbrechen (Browser killt In-Flight-Requests
    // beim Seiten-Unload) — die Praeferenz kommt dann nie beim Server an, und dessen naechste
    // GET-Antwort ueberschreibt den gerade gewaehlten Wert wieder per obigem useEffect. `apiMutate`
    // reicht kein `RequestInit` durch, deshalb hier direkt `apiFetch`. Ein Fehler darf den
    // Ansichtswechsel selbst trotzdem nicht blockieren.
    apiFetch("/api/inquiries/ansicht-praeferenz", {
      method: "PUT",
      body: JSON.stringify({ ansicht: neu }),
      keepalive: true,
    }).catch(() => {});
  }

  // A-04: Der Sidebar-CTA "Neue Anfrage" navigiert zu /anfragen?neu=1. React Router aktualisiert
  // die Search-Params OHNE Remount, daher greift der useState-Initializer nicht erneut. Dieser
  // Effekt oeffnet den Anlege-Drawer reaktiv (und uebernimmt ein Wunschdatum aus ?datum=), auch
  // wenn man bereits auf /anfragen ist.
  useEffect(() => {
    if (params.get("neu") === "1") {
      setCreateOpen(true);
      const datum = params.get("datum");
      if (datum) setForm((f) => ({ ...f, eventDate: datum }));
    }
  }, [params]);

  /**
   * F2 (docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md): `customer-detail.tsx#Angebot erstellen`
   * verlinkt seit dem Umbau auf `/anfragen?neu=1&kunde=:id` statt direkt in den Buchungen-Drawer
   * (der hier keine Kunden-ID mehr entgegennimmt, sondern Name/E-Mail/Adresse wie jede andere
   * Anfrage). Der Kunde wird EINMALIG nachgeladen und die Kontaktfelder vorbefuellt — der Betrieb
   * kann sie wie bei jeder anderen Anfrage noch korrigieren, bevor er anlegt.
   */
  const kundeVorbelegungId = params.get("kunde");
  const kundeVorbelegungQ = useApi<Customer>(kundeVorbelegungId ? `/api/customers/${kundeVorbelegungId}` : null);
  useEffect(() => {
    const ku = kundeVorbelegungQ.data;
    if (!ku) return;
    setForm((f) => ({
      ...f,
      customerName: f.customerName || ku.name,
      customerEmail: f.customerEmail || ku.email,
      company: f.company || ku.company || "",
      phone: f.phone || ku.phone || "",
      addressStreet: f.addressStreet || ku.addressStreet || "",
      addressZip: f.addressZip || ku.addressZip || "",
      addressCity: f.addressCity || ku.addressCity || "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kundeVorbelegungQ.data]);

  /**
   * Rueckwaertskompatibler Redirect. `?open=<inquiryId>` war/ist das Ziel der Glocken-Meldungen,
   * Dashboard-Todos und Kundenprofil-Eintraege — die Route `/anfragen/:id`, auf die hier
   * weitergeleitet wird, ist seit F2 selbst der Umleitungs-Resolver (`App.tsx`, ruft
   * `GET /api/inquiries/:id/vorgang` und leitet auf die eine Vorgangsseite weiter, egal ob `:id`
   * eine Anfrage- oder bereits eine Event-ID ist).
   */
  useEffect(() => {
    const openId = params.get("open");
    if (openId) navigate(`/anfragen/${openId}`, { replace: true });
  }, [params, navigate]);

  /**
   * Die Zeile (Antoine, 21.08.2026): führt direkt zur einen Vorgangsseite. F2: kein Fallback mehr
   * noetig — `inq.id` ist seit AP5 bereits die Event-ID (jede Anfrage hat seit Migration 412
   * sofort einen Vorgang), `generatedEventId` ist deshalb aus dem Typ entfallen.
   *
   * Einheit E (`docs/PLAN-VORGANGSSEITE-UMBAU.md`, 13.09.2026): war bis hierher ein
   * `handleZeileKlick`, das `navigate(...)` rief — die Liste reichte einen KLICK-RUECKRUF nach
   * unten, kein Ziel. Der Browser kannte damit kein Linkziel, also tat Cmd-/Strg-/Mittelklick
   * nichts und „Link in neuem Tab öffnen" fehlte im Kontextmenue. Jetzt reicht die Seite die
   * ADRESSE durch, und die Zeile rendert daraus einen echten Link.
   */
  function zeilenZiel(inq: Inquiry): string {
    return vorgangPfad(inq.id);
  }

  /**
   * P3.3 — die EINE Aktion einer Gruppen-Zeile ruft auf, was es schon gibt (Konzept § 7):
   *
   * - `/vorgaenge/:id/angebot` → Angebots-Builder (`pages/builder.tsx`) mit dem
   *   vorhandenen Verfassen-/Senden-Dialog (`builder.tsx`, `sendOpen`) — „Angebot überprüfen".
   * - `/vorgaenge/:id` → Vorgangs-Detail (`pages/event-detail.tsx`) mit dem
   *   vorhandenen „Nachfassen" (`event-detail.tsx` → `POST /api/events/:id/offers/resend`),
   *   der Adress-/Statuspflege und der Anzahlung — inkl. „KI-Angebot erzeugen"/„Status ändern"
   *   (F2: beides sitzt seit dem Loeschen von `pages/anfrage-detail.tsx` auf DIESER Seite).
   * - `/anfragen/:id` → der Umleitungs-Resolver (`App.tsx`), falls `aktion.ziel` (aus dem
   *   Backend-Paket `@maestro/anfrage-stand`, s. Kommentar dort) ausnahmsweise noch keine
   *   Event-ID kennt.
   *
   * Die Zeile sendet nichts, erzeugt nichts und rechnet nichts. Sie führt hin.
   */
  function fuehreAktionAus(inq: Inquiry, aktion: StandAktion) {
    /**
     * P4.2 — die einzige Aktion, die NICHT hinführt, sondern etwas aufnimmt: „Grund notieren".
     * Es gibt keine Stelle, zu der sie navigieren könnte — der Ablehnungsgrund ist eine Eingabe
     * des Betreibers (Konzept § 4, Lücke L3), und der Statuswechsel, an dem E0.8 ihn entgegennimmt,
     * hat bei einer bereits abgelehnten Zeile längst stattgefunden. Deshalb hier ein Formular
     * statt einer Navigation. Erkannt am SCHLÜSSEL der Regel, nicht am Ziel-Pfad: die Adresse ist
     * eine Anzeige-Entscheidung, der Schlüssel ist der Vertrag.
     */
    if (aktion.schluessel === "grund_notieren") {
      setGrundFehler(null);
      setGrundFuer(inq);
      return;
    }
    // Buchungsstrecke D2: „Jetzt anrufen“ ist kein Router-Ziel (die Knöpfe sind selbst `<a href>`;
    // das hier ist nur der Rückfall, falls ein Aufrufer die Aktion doch durchreicht).
    if (aktion.ziel.startsWith("tel:")) {
      window.location.href = aktion.ziel;
      return;
    }
    if (aktion.ziel.startsWith("/anfragen")) {
      navigate(`/anfragen/${inq.id}`);
      return;
    }
    // `aktion.ziel` kommt aus dem Backend-Paket @maestro/anfrage-stand (bewusst NICHT Teil von
    // F0, s. Kommentar dort) und liefert weiterhin das Alt-Format `/veranstaltungen/...`.
    navigate(uebersetzeAltesZiel(aktion.ziel));
  }

  /**
   * Kanban-"⋯"-Menue (freigegebenes Mockup 11.09.2026,
   * https://claude.ai/code/artifact/d498e616-448e-4432-9247-c8608d335efe) — wie `fuehreAktionAus`
   * fuehrt jeder Punkt nur zu einem bereits bestehenden Ort, nichts wird hier direkt geschrieben:
   *  - "ansehen_bearbeiten": EIN UND DIESELBE Route wie `angebot_bearbeiten`/`angebot_senden`
   *    oben (`aktionBauen` in `@maestro/anfrage-stand`), die Karte beschriftet den Menuepunkt nur
   *    unterschiedlich je nach Versandstatus.
   *  - "notiz": fuehrt zur Vorgangsseite und oeffnet dort automatisch die bestehende
   *    Notizen-Karte (`?notiz=1`, gleiches additive Muster wie `?merge=` in `customer-detail.tsx`).
   *  - "absagen": fuehrt zur Vorgangsseite, wo Storno/Ablehnen-Grund/Status-Menue bereits
   *    vollstaendig existieren — die Karte fuehrt nur hin, wie ueberall sonst in dieser Datei.
   *  - "spam": s. `loescheAlsSpam` unten — einziger Punkt, der selbst etwas ausfuehrt.
   */
  function fuehreMenuAktionAus(inq: Inquiry, aktion: KanbanMenuAktion) {
    // F2: kein `generatedEventId`-Fallback mehr noetig — `inq.id` ist bereits die Event-ID.
    if (aktion === "notiz") {
      navigate(`${vorgangPfad(inq.id)}?notiz=1`);
      return;
    }
    if (aktion === "ansehen_bearbeiten") {
      navigate(angebotPfad(inq.id));
      return;
    }
    if (aktion === "spam") {
      void loescheAlsSpam(inq);
      return;
    }
    if (aktion === "antworten") {
      setAntwortFuer(inq);
      return;
    }
    // "absagen"
    navigate(vorgangPfad(inq.id));
  }

  /**
   * WARTESCHLANGE Punkt 25 (15.09.2026, Antoine): Klick auf die Kanban-Glocke „Erinnerung
   * schicken" — protokolliert NUR den Klick (`POST /api/events/:id/reminder-nudge`,
   * `event.reminder_nudged`), nicht den tatsächlichen Mailversand (der Link daneben ist
   * `mailto:`, s. Kommentar an `erinnerungMailtoHref`, `AnfragenKanban.tsx`). Bewusst NICHT
   * awaited im Klick-Handler der Karte (`AnfragenKanban.tsx#KartenIconKnopf`) — der `mailto:`-Link
   * soll sich ohne Verzögerung öffnen, dieser Aufruf läuft daneben her. Nach Erfolg `reload()`
   * (dasselbe Muster wie `loescheAlsSpam`/`speichereAblehnungsgrund` oben), damit die neue
   * Zeitangabe ohne manuellen Reload sichtbar wird; ein Fehlschlag hier darf das bereits geöffnete
   * Mailprogramm nicht rückgängig machen, wird aber exakt gemeldet statt verschluckt.
   */
  async function erinnerungAnstossen(inq: Inquiry) {
    try {
      await apiMutate(`/api/events/${inq.id}/reminder-nudge`, "POST");
      reload();
    } catch (err) {
      void bestaetigen({ titel: "Das hat nicht geklappt", text: err instanceof Error ? err.message : String(err), bestaetigen: "OK", abbrechen: false });
    }
  }

  /**
   * Antoine 19.10.2026: abgesagte Anfragen zurückholen, auf die der Gast geantwortet hat, ohne
   * dass die Antwort gelesen wurde. Zwei Schritte: Vorschau (`vorschau: true`, schreibt nichts)
   * → Rückfrage mit der Liste → bei Ja derselbe Aufruf ohne Vorschau. Nur für Inhaber (der Server
   * antwortet sonst 403; die Fehlermeldung kommt als Hinweis).
   */
  async function abgesagteZurueckholen() {
    if (zurueckholenLaeuft) return;
    setZurueckholenLaeuft(true);
    try {
      const vorschau = await apiMutate<ZurueckholErgebnis>("/api/inquiries/abgesagte-zurueckholen", "POST", { vorschau: true });
      const holbar = vorschau.kandidaten.filter((k) => k.ziel);
      const uebersprungen = vorschau.kandidaten.filter((k) => !k.ziel);
      if (holbar.length === 0) {
        await bestaetigen({
          titel: "Nichts zum Zurückholen",
          text:
            uebersprungen.length > 0
              ? `${uebersprungen.length} abgesagte ${uebersprungen.length === 1 ? "Anfrage" : "Anfragen"} mit Gast-Antwort ${uebersprungen.length === 1 ? "lässt" : "lassen"} sich nicht automatisch zurückholen. Bitte im Vorgang selbst prüfen.`
              : "Keine von Hand abgesagte Anfrage der letzten 7 Tage hat eine ungelesene Antwort vom Gast.",
          bestaetigen: "OK",
          abbrechen: false,
        });
        return;
      }
      const ok = await bestaetigen({
        titel: `${holbar.length} ${holbar.length === 1 ? "Anfrage" : "Anfragen"} zurückholen?`,
        text: (
          <div className="space-y-2">
            <p>Diese Anfragen hast du in den letzten 7 Tagen abgesagt, obwohl der Gast geantwortet hatte. Sie gehen zurück auf ihren vorherigen Stand.</p>
            <ul data-testid="zurueckholen-liste" className="list-disc space-y-1 pl-5">
              {holbar.map((k) => (
                <li key={k.id}>
                  <strong>{k.customerName}</strong>
                  {k.eventDate ? `, Termin ${formatDate(k.eventDate)}` : ""} → {eventStatusMeta(k.ziel!).label}
                </li>
              ))}
            </ul>
            {uebersprungen.length > 0 && (
              <p>
                Nicht automatisch zurückholbar ({uebersprungen.length}): {uebersprungen.map((k) => k.customerName).join(", ")}.
              </p>
            )}
          </div>
        ),
        bestaetigen: "Zurückholen",
      });
      if (!ok) return;
      const echt = await apiMutate<ZurueckholErgebnis>("/api/inquiries/abgesagte-zurueckholen", "POST", {});
      reload();
      await bestaetigen({
        titel: `${echt.zurueckgeholt} ${echt.zurueckgeholt === 1 ? "Anfrage" : "Anfragen"} zurückgeholt`,
        bestaetigen: "OK",
        abbrechen: false,
      });
    } catch (err) {
      void bestaetigen({ titel: "Das hat nicht geklappt", text: err instanceof Error ? err.message : String(err), bestaetigen: "OK", abbrechen: false });
    } finally {
      setZurueckholenLaeuft(false);
    }
  }

  /**
   * F2 (docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md, Antoines Mockup-Freigabe 13.09.2026):
   * "Als Spam löschen" aus dem Kanban-Menue. Bewusst ein einfacher window.confirm OHNE Abtippen
   * eines Bestaetigungsworts (dasselbe Muster wie abrechnung.tsx#paymentAction "delete" — Antoine
   * hatte diese Verschaerfung im Mockup-Review offen gelassen, nicht explizit verlangt). Ruft
   * DELETE /api/events/:id — derselbe geteilte Loeschpfad wie ueberall sonst
   * (lib/vorgang-loeschen.ts), blockiert also weiterhin auf vorhandene Angebotsvarianten (409),
   * falls die Karte zwischenzeitlich veraltet ist.
   */
  async function loescheAlsSpam(inq: Inquiry) {
    if (
      !(await bestaetigen({
        titel: "Diese Anfrage als Spam löschen?",
        text: "Der leere Vorgang und die Anfrage werden entfernt. Der angelegte Kundendatensatz bleibt bestehen (Profil im Kundenbereich), falls er noch für andere Vorgänge gebraucht wird.",
        bestaetigen: "Als Spam löschen",
        gefahr: true,
      }))
    )
      return;
    try {
      await apiMutate(`/api/events/${inq.id}`, "DELETE");
      reload();
    } catch (err) {
      void bestaetigen({ titel: "Das hat nicht geklappt", text: err instanceof Error ? err.message : String(err), bestaetigen: "OK", abbrechen: false });
    }
  }

  /**
   * P4.2 — den notierten Grund speichern. Ruft `POST /api/events/:id/ablehnungsgrund` (die
   * Route, die ihn in dieselbe Senke schreibt wie E0.8: `activity_log.details.grund`) und lädt
   * die Liste neu. Der Satz nennt den Grund danach von selbst — er kommt aus `leiteStandAb`,
   * nicht von hier.
   */
  async function speichereAblehnungsgrund(grund: string) {
    const inq = grundFuer;
    if (!inq) return;
    // F2: `inq.id` ist bereits die Event-ID — jede Anfrage hat seit Migration 412 einen Vorgang,
    // der fruehere "kein Vorgang"-Gürtel (`generatedEventId`-Check) ist damit gegenstandslos.
    setGrundBusy(true);
    setGrundFehler(null);
    try {
      await apiMutate(`/api/events/${inq.id}/ablehnungsgrund`, "POST", { grund });
      setGrundFuer(null);
      reload();
    } catch (err) {
      setGrundFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setGrundBusy(false);
    }
  }

  const inquiries = data ?? [];
  // Der Knopf „Abgesagte mit Gast-Antwort zurückholen“ erscheint nur, wenn die Liste so einen Fall zeigt.
  const kannZurueckholen = inquiries.some((i) => i.tatsachen?.eventStatus === "cancelled" && Boolean(i.tatsachen?.ungeleseneGastAntwortAm));
  /* P10.6 (32a, § 12.2 Punkt 2) — hier standen zwei weitere Zähler: „N Anfragen in den letzten
     90 Tagen" und „N offen". Beide sind entfallen.
     „offen" ist das Wort aus dem Widerspruch, den 32a abschafft (§ 12.1: Badge `6` · „6 offen" ·
     „bei 17 wartet etwas auf dich") — drei Zahlen über dieselbe Liste, von denen keine zwei
     dasselbe meinten. Der 90-Tage-Zähler war die dritte: er zählte eine Menge, die gar nicht auf
     dem Bildschirm steht (die Liste zeigt ALLE Anfragen, § 12.6 „kein Datenfenster").
     Gezählt wird jetzt an genau einer Stelle — der Kopfzeile der Liste in
     `AnfragenGruppenAnsicht` —, und das Seitenleisten-Badge wiederholt dieselbe Zahl. */

  // Welche Quellen kommen in den Daten vor -> nur diese im Filter anbieten.
  // W2.5: zusaetzlich je vorkommendem Formular-Widget ein eigener Eintrag ("Formular ‚Catering'"),
  // damit der Betreiber sehen kann, was EIN bestimmtes Formular einbringt. Rein clientseitig auf
  // der ohnehin geladenen Liste — keine neue Route, kein neuer Query-Parameter.
  const availableSources = useMemo(() => {
    const eintraege = Array.from(new Set(inquiries.map((i) => i.source).filter(Boolean))).map((s) => ({
      value: s,
      label: SOURCE_SHORT[s] ?? inquirySourceMeta(s).label,
    }));
    const widgets = new Map<string, string>();
    for (const i of inquiries) {
      const name = i.widgetName?.trim();
      if (i.widgetId && name && !widgets.has(i.widgetId)) widgets.set(i.widgetId, name);
    }
    for (const [id, name] of widgets) eintraege.push({ value: `${WIDGET_FILTER}${id}`, label: `Formular „${name}“` });
    return eintraege;
  }, [inquiries]);

  // P10.1: Suche und Filter wirken auf die GANZE Liste — es gibt keine Reiter-Vorauswahl mehr,
  // die vorher schon die Hälfte weggenommen hätte.
  const searched = useMemo(() => {
    const q = query.trim().toLowerCase();
    const now = Date.now();
    return inquiries.filter((i) => {
      // R0.2: die Firma ist mitdurchsuchbar — wer eine Firmen-Pille sieht, sucht als Naechstes
      // nach genau diesem Namen.
      if (
        q &&
        // Reisegruppen-Widget T6: die Anfragenummer „A-3F9C21“ aus der Bestätigungsmail (erste sechs Stellen der ID) findet die Anfrage.
        !`${i.customerName} ${i.customerEmail ?? ""} ${i.company ?? ""} ${i.message ?? ""} a-${i.id.replace(/[^0-9a-f]/gi, "").slice(0, 6)}`
          .toLowerCase()
          .includes(q)
      )
        return false;
      if (sourceFilter.startsWith(WIDGET_FILTER)) {
        if (i.widgetId !== sourceFilter.slice(WIDGET_FILTER.length)) return false;
      } else if (sourceFilter !== "all" && i.source !== sourceFilter) return false;
      if (dateFilter !== "all") {
        const t = i.eventDate ? new Date(i.eventDate).getTime() : null;
        if (t == null) return false;
        if (dateFilter === "upcoming" && t < now) return false;
        if (dateFilter === "past" && t >= now) return false;
      }
      // Die Lage zuletzt: sie ist die teuerste Frage und muss nur noch beantwortet werden, was
      // Suche und die beiden älteren Filter übrig gelassen haben.
      if (!trifftLage(i, lageFilter, now)) return false;
      if (!trifftNotiz(i, notizFilter)) return false;
      return true;
    });
  }, [inquiries, query, sourceFilter, dateFilter, lageFilter, notizFilter]);
  const notizOptionen = useMemo(() => notizFilterOptionen(inquiries), [inquiries]);

  // Kanban braucht dieselben Abschnitte wie die Liste (`AnfragenGruppenAnsicht` intern) — dieselben
  // exportierten, reinen Funktionen aus `@maestro/anfrage-stand`-gestuetzter Gruppierung, keine
  // zweite Ableitung. Nur berechnet, wenn tatsaechlich Kanban aktiv ist.
  const kanbanAbschnitte = useMemo(
    () =>
      ansicht === "kanban"
        ? fasseFuerAnzeigeZusammen(teileInGruppen(searched).gruppen).map((a) => ({ ...a, zeilen: sort.sortiere(a.zeilen) }))
        : [],
    // `sort` ist je Render ein neues Objekt; was zählt, sind Spalte und Richtung.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ansicht, searched, sort.sortKey, sort.dir],
  );

  function closeCreate() {
    setCreateOpen(false);
    setForm(EMPTY_FORM);
    setKiText("");
    setKiNote(null);
    setKiAnlass("");
    setKiDiaet([]);
    setKanal("");
    setFormError(null);
    params.delete("neu");
    setParams(params, { replace: true });
  }

  async function handleCreate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!kanal) {
      setFormError("Bitte wählen, wie die Anfrage hereinkam: Telefon, E-Mail oder persönlich / sonstiges.");
      return;
    }
    setBusy(true);
    setFormError(null);
    try {
      await apiMutate("/api/inquiries", "POST", {
        kanal,
        customerName: form.customerName,
        customerEmail: form.customerEmail,
        // R0.2: leer lassen heisst "keine Firma" — nicht Leerstring senden.
        company: form.company.trim() || undefined,
        // Migration 383: dieselbe Konvention — leer lassen heisst "nicht angegeben".
        phone: form.phone.trim() || undefined,
        addressStreet: form.addressStreet.trim() || undefined,
        addressZip: form.addressZip.trim() || undefined,
        addressCity: form.addressCity.trim() || undefined,
        eventDate: form.eventDate ? new Date(form.eventDate).toISOString() : undefined,
        guests: form.guests ? Number(form.guests) : undefined,
        message: form.message || undefined,
        /**
         * §5.16: Anlass und Diät-Anforderungen gehen als EIGENE Felder mit (nach
         * `inquiries.event_type` bzw. `dietary_requirements`), nicht nur als Textschnipsel
         * vorn an der Nachricht. Vorher landeten sie ausschließlich dort — und ein Anlass
         * mitten im Fließtext ist für den Komponisten kein Anlass, sondern Prosa.
         */
        occasion: kiAnlass || undefined,
        dietaryRequirements: kiDiaet.length > 0 ? kiDiaet : undefined,
      });
      closeCreate();
      reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  /** KI-Import: eingefügten E-Mail-/Freitext parsen und die Formularfelder vorbefüllen. */
  async function importFromText() {
    if (!kiText.trim()) return;
    setKiBusy(true);
    setKiNote(null);
    setFormError(null);
    try {
      const parsed = await apiMutate<Partial<CreateForm & { occasion: string; dietaryRequirements: string[] }>>(
        "/api/inquiries/parse-text",
        "POST",
        { text: kiText },
      );
      const hasAny = parsed && Object.values(parsed).some((v) => v != null && v !== "");
      if (!hasAny) {
        setKiNote("Aus dem Text ließen sich keine Felder sicher erkennen – bitte manuell ausfüllen.");
        return;
      }
      // Nur erkannte Felder übernehmen; Bestehendes nicht mit Leerwerten überschreiben.
      setForm((f) => ({
        customerName: parsed.customerName || f.customerName,
        customerEmail: parsed.customerEmail || f.customerEmail,
        // Der Parser (lib/inquiry-parse.ts) kennt heute KEIN eigenes Firmenfeld — eine aus dem
        // Freitext erkannte Firma waere eine Vermutung und braucht die Kennzeichnung aus
        // Konzept § 5.3 (3). Bis dahin bleibt hier stehen, was der Mensch eingetragen hat.
        company: f.company,
        // Migration 383: Telefon/Anschrift wie die uebrigen Felder — nur erkannte Werte
        // uebernehmen, Bestehendes (bereits von Hand korrigiertes) nicht mit Leerwerten ueberschreiben.
        phone: parsed.phone || f.phone,
        addressStreet: parsed.addressStreet || f.addressStreet,
        addressZip: parsed.addressZip || f.addressZip,
        addressCity: parsed.addressCity || f.addressCity,
        eventDate: parsed.eventDate || f.eventDate,
        guests: parsed.guests != null ? String(parsed.guests) : f.guests,
        // Anlass vorne an die Nachricht stellen, falls erkannt und noch nicht enthalten.
        message:
          parsed.message ||
          [parsed.occasion, f.message].filter(Boolean).join(parsed.occasion && f.message ? " – " : "") ||
          f.message,
      }));
      // §5.16: strukturiert festhalten, was erkannt wurde — beim Anlegen geht es als eigenes
      // Feld mit. Der Hinweis unten nennt es, damit der Mensch es prüfen kann statt es zu ahnen.
      setKiAnlass(parsed.occasion?.trim() || "");
      setKiDiaet(Array.isArray(parsed.dietaryRequirements) ? parsed.dietaryRequirements : []);
      const erkannt = [
        parsed.occasion?.trim() ? `Anlass „${parsed.occasion.trim()}“` : null,
        Array.isArray(parsed.dietaryRequirements) && parsed.dietaryRequirements.length > 0
          ? `Ernährung: ${parsed.dietaryRequirements.map((d) => DIAET_TEXT[d] ?? d).join(", ")}`
          : null,
      ].filter(Boolean);
      setKiNote(
        erkannt.length > 0
          ? `Felder übernommen – bitte prüfen und ggf. korrigieren. Zusätzlich erkannt: ${erkannt.join(" · ")}.`
          : "Felder übernommen – bitte prüfen und ggf. korrigieren.",
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setKiNote(/ai_unavailable|nicht konfiguriert|503/i.test(msg) ? "KI-Import ist nicht konfiguriert." : msg);
    } finally {
      setKiBusy(false);
    }
  }

  /*
   * Punkt 79, das Aussehen (Entwurf 40b): die drei Zahlen der Kopfzeile. Sie entstehen aus der
   * BEREITS GELADENEN Liste — dieselbe, aus der das Board seine Spalten baut. Keine neue
   * Abfrage, keine neue Funktion.
   *
   * Nichts wird behauptet, was nicht dasteht: ohne Betraege entfaellt die Geldangabe, ohne
   * ueberfaellige Anzahlung der dritte Teil. Eine Zeile „0,00 € im Board" waere dieselbe Sorte
   * Rauschen, die Punkt 76 gerade beseitigt hat.
   */

  return (
    <>
      {bestaetigungsDialog}
      <PageHeader
        title="Anfragen"
        subtitle="Verwalten und priorisieren Sie alle eingehenden Kundenanfragen."
        actions={
          <div className="flex flex-wrap items-center gap-2.5">
            {kannZurueckholen && (
              <Button type="button" variant="secondary" data-testid="abgesagte-zurueckholen" disabled={zurueckholenLaeuft} onClick={() => void abgesagteZurueckholen()}>
                <Icon name="undo" className="text-[18px]" /> Abgesagte mit Gast-Antwort zurückholen
              </Button>
            )}
            <Button type="button" onClick={() => setCreateOpen(true)}>
              <Icon name="add" className="text-[18px]" /> Anfrage anlegen
            </Button>
          </div>
        }
      />

      {/* F0 (docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md Teil B2): die Infobox "historisch
          importierte Vorgaenge mit Status Anfrage liegen unter Veranstaltungen" ist entfernt.
          Nach dem in diesem Plan beschlossenen Umbau bekommt JEDE Anfrage ab Eingang sofort einen
          Vorgang (Antoines D1) - der Sonderfall "Alt-Anfrage ohne Vorgang, nur unter der
          Veranstaltungsliste auffindbar" verschwindet damit strukturell, nicht nur im Wortlaut.
          Ein dauerhafter Hinweis auf eine Liste, die es namentlich nicht mehr gibt ("Veranstaltungen"),
          waere ab hier irrefuehrend statt hilfreich. */}

      {/* P10.1 (32a) — die EINE Liste. Suche und die beiden Filter stehen über allen Gruppen und
          wirken auf alle; darunter die Arbeitsliste aus `@maestro/anfrage-stand`. Welche Gruppe,
          welcher Satz und welche Aktion — das sagt die Regel, nicht diese Datei.

          P10.5: Die Werkzeugleiste behält ihre Karte, die Gruppen bringen ihre eigenen mit
          (`AnfragenGruppenAnsicht`). Eine gemeinsame Umschlagkarte gibt es nicht mehr — sonst
          stünden Karten in einer Karte. */}
      <div className="flex flex-col gap-4">
        {error && (
          <Card className="overflow-hidden">
            <ErrorNote message={error} onRetry={reload} />
          </Card>
        )}
        {loading ? (
          <Card className="overflow-hidden">
            <LoadingRows />
          </Card>
        ) : inquiries.length === 0 ? (
          <Card className="overflow-hidden">
            <EmptyState
              icon="inbox"
              title="Keine Anfragen"
              hint="Sobald über Ihr Anfrageformular oder per E-Mail eine Anfrage eingeht, erscheint sie automatisch hier."
              cta={{ label: "Anfrage manuell anlegen", icon: "add", onClick: () => setCreateOpen(true) }}
            />
          </Card>
        ) : (
          <>
            {/* Toolbar (E2, seit P10.1 über den Gruppen): Suche + Wunschdatum- + Quelle-Filter.
                Redesign 08.09.2026 (Kanban-Mockup, freigegeben) — dieselbe Funktion wie zuvor
                (Werte/Handler/Optionen unveraendert), nur als abgerundete Icon-Pillen-Chips statt
                rohen Kaesten: aktive Filter (Wert != "all") heben sich mit Akzentfarbe ab. */}
            <Card className="flex flex-wrap items-center gap-2.5 px-[18px] py-3">
              {/* Entwurf 40b: das Suchfeld trägt dieselbe weiche Ecke wie alles andere in der
                  Leiste (`border-radius:9px`), keine volle Pille. */}
              <div className="flex min-w-[220px] items-center gap-2 rounded-[9px] border border-outline-variant bg-surface-container-low px-3.5 py-1.5 dark:bg-surface-variant/10">
                <Icon name="search" className="text-[16px] text-on-surface-variant/70" />
                <input
                  className="w-full bg-transparent text-[12px] text-on-surface outline-none placeholder:text-on-surface-variant/60 dark:placeholder:text-on-surface-variant/80 dark:text-inverse-on-surface"
                  placeholder="In Anfragen suchen …"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              {/*
                Die drei Filter-Chips laufen seit dem 21.09.2026 durch EINEN Baustein
                (`ChipSelect`, `ui/components.tsx`) statt durch dreimal fast gleiches Markup.
                Anlass: der neue Lage-Filter liess den Rohbestand an nativem Auswahlfeld-Markup von 16 auf 17
                steigen, und die Design-Ratsche hat das sofort gemeldet — ihr Ziel ist 0. Die Zahl
                hochzusetzen waere eine gelockerte Pruefung gewesen; der Baustein zieht sie
                stattdessen auf 14. Aussehen und Verhalten sind unveraendert.
              */}
              <ChipSelect
                wert={dateFilter}
                onWechsel={(w) => setDateFilter(w as typeof dateFilter)}
                icon="event"
                ariaLabel="Wunschdatum"
                optionen={[
                  { wert: "all", text: "Wunschdatum: Alle" },
                  { wert: "upcoming", text: "Wunschdatum: Kommende" },
                  { wert: "past", text: "Wunschdatum: Vergangene" },
                ]}
              />
              <ChipSelect
                wert={sourceFilter}
                onWechsel={setSourceFilter}
                icon="filter_alt"
                ariaLabel="Quelle"
                optionen={[
                  { wert: "all", text: "Quelle: Alle" },
                  ...availableSources.map((s) => ({ wert: s.value, text: `Quelle: ${s.label}` })),
                ]}
              />
              {/* Die LAGE-Filter (21.09.2026, Antoine). EIN Auswahlfeld, weil er auf die Frage nach
                  Kombinierbarkeit „Immer nur einer" geantwortet hat: ein Feld erzwingt das, statt
                  es zu verbieten. Was die einzelnen Einträge bedeuten, steht an `trifftLage`. */}
              <ChipSelect
                testId="lage-filter"
                wert={lageFilter}
                onWechsel={(w) => setLageFilter(w as LageFilter)}
                icon="flag"
                ariaLabel="Lage"
                optionen={LAGE_FILTER_TEXT}
              />
              {/* Notizen-Filter (25.10.2026, Mockup): eigenes Feld, kombinierbar mit „Lage“. */}
              <ChipSelect
                testId="notiz-filter"
                wert={notizFilter}
                onWechsel={(w) => setNotizFilter(w as NotizFilter)}
                icon="sticky_note_2"
                ariaLabel="Notizen"
                optionen={notizOptionen}
              />
              {/* Die Sortierung: in der Liste über die Spaltenköpfe (Antoine 23.09.2026, sie ersetzen
                  die Auswahl vom 21.09.). Kanban und Telefon haben keine Spaltenköpfe — dort
                  bleibt dasselbe als Menü (Brief § 9), mit demselben gemerkten Zustand. */}
              <SortierMenue
                className={ansicht === "kanban" ? "" : "md:hidden"}
                optionen={ANFRAGEN_SORTIERUNG}
                sortKey={sort.sortKey}
                dir={sort.dir}
                onWahl={sort.setze}
              />
              <span className="font-body-sm text-[11.5px] text-on-surface-variant/70">
                Suche und Filter wirken auf alle Gruppen
              </span>

              {/* Kanban-Umschalter (sql/358) — reine Darstellungswahl, keine zweite Datenquelle:
                  dieselben Gruppen wie die Liste, nur nebeneinander statt gestapelt.

                  AUSSEHEN NACH ENTWURF 40b (21.09.2026): dort ist das ein eingelassenes Segment —
                  gedämpfte Hülle (`#F6F1E7`), Ecken 9px außen und 7px innen, und die GEWÄHLTE
                  Seite hebt sich hell und erhaben ab (weiß + weicher Schatten) statt in der
                  Akzentfarbe zu leuchten. Der Akzentton bleibt damit dem EINEN Knopf vorbehalten,
                  der etwas auslöst („Anfrage anlegen"); eine Ansichtswahl löst nichts aus.

                  Der Umschalter BLEIBT in dieser Leiste. Im Entwurf sitzt er eine Ebene höher, in
                  der obersten Leiste neben der Suche — die gehört bei uns aber ALLEN Seiten. Einen
                  Umschalter, den es nur auf dieser einen Seite gibt, dort einzubauen, wäre ein
                  Eingriff in das Gerüst und nicht ins Aussehen. Antoine am 21.09.2026: „Es geht
                  nur ums aussehen!!!" Die Stelle rechts außen ist dieselbe wie im Entwurf.

                  `aria-pressed` bleibt unverändert: was gewählt ist, sagt der Oberfläche weiterhin
                  nicht die Farbe allein. */}
              <div className="ml-auto flex items-center gap-1 rounded-[9px] bg-surface-container-high p-[3px] dark:bg-white/[0.08]">
                <button
                  type="button"
                  onClick={() => wechsleAnsicht("liste")}
                  aria-pressed={ansicht === "liste"}
                  className={`inline-flex items-center gap-1 rounded-[7px] px-3 py-1 text-[12px] font-semibold motion-safe:transition-colors ${
                    ansicht === "liste"
                      ? "bg-surface-container-lowest text-on-surface shadow-sm dark:bg-inverse-surface dark:text-inverse-on-surface"
                      : "text-on-surface-variant hover:bg-surface-container-lowest/50 dark:hover:bg-inverse-surface/40"
                  }`}
                >
                  <Icon name="view_list" className="text-[15px]" /> Liste
                </button>
                <button
                  type="button"
                  onClick={() => wechsleAnsicht("kanban")}
                  aria-pressed={ansicht === "kanban"}
                  className={`inline-flex items-center gap-1 rounded-[7px] px-3 py-1 text-[12px] font-semibold motion-safe:transition-colors ${
                    ansicht === "kanban"
                      ? "bg-surface-container-lowest text-on-surface shadow-sm dark:bg-inverse-surface dark:text-inverse-on-surface"
                      : "text-on-surface-variant hover:bg-surface-container-lowest/50 dark:hover:bg-inverse-surface/40"
                  }`}
                >
                  <Icon name="view_kanban" className="text-[15px]" /> Kanban
                </button>
              </div>
            </Card>

            {searched.length === 0 ? (
              <Card className="overflow-hidden">
                {/* Punkt 115 (Paket 1): nie ohne Ausweg — Suche/Filter aufheben, und weil ein
                    gebuchter Fall nicht mehr unter Anfragen steht, die ⌘K-Suche über alle Bereiche. */}
                <EmptyState
                  icon="search_off"
                  title={query.trim() ? `Keine Anfrage passt zu „${query.trim()}“` : "Keine Treffer"}
                  hint={
                    query.trim()
                      ? "Ist der Fall schon zugesagt, steht er unter Buchungen — die Suche über alle Bereiche findet ihn."
                      : "Passen Sie die Filter an."
                  }
                  link={[
                    ...(query.trim() ? [{ label: "In allen Bereichen suchen", onClick: () => oeffneGlobaleSuche(query.trim()) }] : []),
                    ...(query.trim() ? [{ label: "Suche aufheben", onClick: () => setQuery("") }] : []),
                    ...(dateFilter !== "all" || sourceFilter !== "all" || lageFilter !== "all" || notizFilter !== "all"
                      ? [
                          {
                            label: "Filter aufheben",
                            onClick: () => setzeZustand({ zeitraum: "all", quelle: "all", lage: "all", notiz: "all" }),
                          },
                        ]
                      : []),
                  ]}
                />
              </Card>
            ) : ansicht === "kanban" ? (
              <AnfragenKanban
                abschnitte={kanbanAbschnitte}
                zeilenZiel={zeilenZiel}
                onAktion={fuehreAktionAus}
                onMenuAktion={fuehreMenuAktionAus}
                onErinnerungSchicken={(inq) => void erinnerungAnstossen(inq)}
              />
            ) : (
              <AnfragenGruppenAnsicht
                anfragen={searched}
                gesamt={inquiries.length}
                zeilenZiel={zeilenZiel}
                onAktion={fuehreAktionAus}
                onAntworten={setAntwortFuer}
              />
            )}
          </>
        )}
      </div>

      <AnfrageAntwortDrawer anfrage={antwortFuer} onClose={() => setAntwortFuer(null)} onGesendet={reload} />

      {/* Anlegen-Drawer */}
      <Drawer open={createOpen} onClose={closeCreate} title="Anfrage anlegen">
        {/* ✨ KI-Import: E-Mail/Freitext einfügen -> Felder werden vorbefüllt. */}
        <div className="mb-6 rounded-xl border border-primary/30 bg-primary-container/20 p-4 dark:bg-primary-container/10">
          <div className="mb-1 flex items-center gap-2">
            <Icon name="auto_awesome" className="text-[18px] text-primary" />
            <span className="font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
              KI-Import
            </span>
          </div>
          <p className="mb-3 font-body-sm text-xs text-secondary dark:text-secondary-fixed-dim">
            E-Mail oder Freitext der Anfrage einfügen – die KI füllt die Felder unten aus. Bitte danach prüfen.
          </p>
          <Textarea
            rows={4}
            placeholder="Text der Anfrage hier einfügen (z. B. eine weitergeleitete E-Mail) …"
            value={kiText}
            onChange={(e) => setKiText(e.target.value)}
          />
          <div className="mt-2 flex items-center justify-between gap-3">
            <span className="font-body-sm text-xs text-secondary dark:text-secondary-fixed-dim">{kiNote ?? ""}</span>
            <Button
              type="button"
              variant="secondary"
              className="shrink-0"
              disabled={kiBusy || !kiText.trim()}
              onClick={() => void importFromText()}
            >
              <Icon name="auto_awesome" className="text-[16px]" /> {kiBusy ? "Liest …" : "Aus Text übernehmen"}
            </Button>
          </div>
        </div>

        <form className="space-y-4" onSubmit={(e) => void handleCreate(e)}>
          {formError && <ErrorNote message={formError} />}
          <fieldset data-testid="kanal-auswahl">
            <legend className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
              Wie kam die Anfrage herein? *
            </legend>
            <div className="flex flex-wrap gap-2">
              {KANAL_OPTIONEN.map((o) => (
                <label
                  key={o.value}
                  className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1.5 font-label-md text-[12.5px] font-semibold transition-colors ${
                    kanal === o.value
                      ? "border-primary bg-primary-fixed text-on-primary-fixed"
                      : "border-outline-variant/60 text-on-surface-variant hover:border-primary"
                  }`}
                >
                  <Radio
                    name="kanal"
                    value={o.value}
                    required
                    className="sr-only"
                    checked={kanal === o.value}
                    onChange={() => setKanal(o.value)}
                  />
                  <Icon name={o.icon} className="text-[15px]" />
                  {o.label}
                </label>
              ))}
            </div>
          </fieldset>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
              Kundenname *
            </span>
            <Input
              required
              placeholder="z. B. Meier & Weber GmbH"
              value={form.customerName}
              onChange={(e) => setForm((f) => ({ ...f, customerName: e.target.value }))}
            />
          </label>
          {/*
            R0.2 — ohne dieses Feld traegt nur das Website-Intake je eine Firma; alles hier
            Angelegte bliebe ohne Firma, und die Pille „Firmenkunde“ saehe aus wie ein Fehler.
            Bewusst optional (P-2: Firmenfeld ist nie Pflicht).
          */}
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
              Firma <span className="font-normal text-on-surface-variant">(optional)</span>
            </span>
            <Input
              maxLength={200}
              placeholder="z. B. Weber Immobilien AG"
              value={form.company}
              onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
            />
            <Zeichenzaehler current={form.company.length} max={200} />
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
              E-Mail *
            </span>
            <Input
              required
              type="email"
              placeholder="kontakt@beispiel.de"
              value={form.customerEmail}
              onChange={(e) => setForm((f) => ({ ...f, customerEmail: e.target.value }))}
            />
          </label>
          {/*
            Migration 383 (Antoine, STORIA) — Telefon + Anschrift aus dem E-Mail-Footer. Beide vom
            KI-Import vorbefuellbar (importFromText), bewusst optional (telefonische/postalische
            Anfragen ohne vollstaendige Kontaktdaten sind kein Fehlerfall).
          */}
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
              Telefon <span className="font-normal text-on-surface-variant">(optional)</span>
            </span>
            <Input
              type="tel"
              maxLength={60}
              placeholder="z. B. 030 1234567"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            />
          </label>
          <div className="space-y-2">
            <span className="block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
              Adresse <span className="font-normal text-on-surface-variant">(optional)</span>
            </span>
            <Input
              maxLength={200}
              placeholder="Straße und Hausnummer"
              value={form.addressStreet}
              onChange={(e) => setForm((f) => ({ ...f, addressStreet: e.target.value }))}
            />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input
                maxLength={32}
                placeholder="PLZ"
                value={form.addressZip}
                onChange={(e) => setForm((f) => ({ ...f, addressZip: e.target.value }))}
              />
              <Input
                maxLength={200}
                placeholder="Ort"
                value={form.addressCity}
                onChange={(e) => setForm((f) => ({ ...f, addressCity: e.target.value }))}
              />
            </div>
          </div>
          {/* Bughunt 28.08.2026 (Antoine, Screenshot): "Wunschdatum"/"Gäste" überlappten sich auf
              dem Handy — die feste `grid-cols-2` zwang beide Felder auch bei 375-390px Breite
              nebeneinander (das native Datums-Feld allein braucht dort schon fast die volle
              Breite). Jetzt wie der Rest des Repos (`sm:grid-cols-2`, s. NachschlagenTabs.tsx/
              agent-cards.tsx): einspaltig bis `sm`, danach nebeneinander. */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
                Wunschdatum
              </span>
              <DateInput
                value={form.eventDate}
                onValueChange={(v) => setForm((f) => ({ ...f, eventDate: v }))}
              />
              {/* V-1304: ein Wunschdatum in der Vergangenheit ist beim Anlegen fast immer ein
                  Tippfehler im Jahr — sichtbar machen, mit einem Klick korrigierbar. */}
              <DatumVergangenHinweis
                datumIso={form.eventDate}
                testId="anfrage-datum-vergangen"
                onKorrigieren={(iso) => setForm((f) => ({ ...f, eventDate: iso }))}
              />
            </label>
            <label className="block">
              <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
                Gäste
              </span>
              <Input
                type="number"
                min="1"
                // Bughunt 28.08.2026: "80" allein sah wie ein bereits eingetragener Wert aus,
                // nicht wie ein Platzhalter — dieselbe "z. B. …"-Konvention wie Kundenname/Firma
                // oben in diesem Formular macht die Beispielhaftigkeit eindeutig.
                placeholder="z. B. 80"
                value={form.guests}
                onChange={(e) => setForm((f) => ({ ...f, guests: e.target.value }))}
              />
            </label>
          </div>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
              Nachricht
            </span>
            <Textarea
              rows={5}
              placeholder="Worum geht es? (Anlass, Wünsche, Budget …)"
              value={form.message}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            />
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={closeCreate}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? "Speichert …" : "Anfrage anlegen"}
            </Button>
          </div>
        </form>
      </Drawer>

      {/* P4.2 — „Grund notieren": das einzige Formular dieser Ansicht. Es nimmt auf, was nur der
          Betreiber weiß; der Satz nennt es danach von selbst (`@maestro/anfrage-stand`). */}
      <GrundNotierenDialog
        open={grundFuer !== null}
        kundenName={grundFuer?.customerName ?? ""}
        satz={grundFuer?.stand?.satz ?? null}
        busy={grundBusy}
        fehler={grundFehler}
        onClose={() => {
          setGrundFuer(null);
          setGrundFehler(null);
        }}
        onSpeichern={speichereAblehnungsgrund}
      />
    </>
  );
}
